<script lang="ts">
  import { onDestroy, untrack } from 'svelte';
  import type { Candidate } from '$types';
  import type { PickerFeed } from './picker.svelte';
  import { prefs } from './prefs.svelte';
  import { QUALITY_LEVELS, probedQuality, qualityAvailability, qualityLabel, type SelectedQuality } from './quality';

  interface Props {
    feed: PickerFeed;
    onchosen?: () => void;
  }
  const { feed, onchosen }: Props = $props();
  let choosing = $state<SelectedQuality | null>(null);
  let error = $state('');
  let disposed = false;
  const active = $derived.by(() => {
    const c = feed.candidates.find(c => c.id === feed.activeId);
    if (!c) return undefined;
    const status = feed.statusOf(c);
    return status.state === 'good' ? probedQuality(status.probe) : c.quality;
  });
  let checking = $state(false);
  function probeFor(c: Candidate) {
    const status = feed.statusOf(c);
    return status.state === 'good' ? status.probe : status.state === 'bad' ? null : undefined;
  }
  const choices = $derived(QUALITY_LEVELS.map(quality => ({
    quality, ...qualityAvailability(feed.candidates, quality, prefs.audioLang, probeFor, { checking })
  })));
  async function verifyChoices(): Promise<void> {
    if (checking || disposed) return;
    checking = true;
    try {
      await feed.checkMany(feed.candidates);
    } catch {
      // Failed checks remain unverified; never enable a tier without evidence.
    } finally { if (!disposed) checking = false; }
  }
  $effect(() => {
    const candidates = feed.candidates;
    const resolving = feed.resolving;
    const language = prefs.audioLang;
    if (!resolving && candidates.length) untrack(() => { void language; void verifyChoices(); });
  });

  onDestroy(() => { disposed = true; feed.cancelChecks(); });

  async function choose(quality: SelectedQuality): Promise<void> {
    if (choosing !== null || qualityAvailability(feed.candidates, quality, prefs.audioLang, probeFor).disabled) return;
    choosing = quality;
    error = '';
    try {
      let chosen = false;
      if (feed.chooseQuality) chosen = await feed.chooseQuality(quality);
      else {
        const options = qualityAvailability(feed.candidates, quality, prefs.audioLang, probeFor).candidates;
        const deadline = Date.now() + 30_000;
        for (const c of options.slice(0, 12)) {
          if (disposed) return;
          if (Date.now() >= deadline) break;
          if (await feed.choose(c, quality)) { chosen = true; break; }
        }
      }
      if (disposed) return;
      if (chosen) {
        prefs.maxQuality = quality;
        onchosen?.();
      } else error = `No working ${qualityLabel(quality)} copy was found. Try another quality or refresh.`;
    } catch {
      if (!disposed) error = 'Could not switch quality. Your current video has been kept.';
    } finally {
      if (!disposed) choosing = null;
    }
  }

  async function refresh(): Promise<void> {
    error = '';
    try { await feed.refresh(); await verifyChoices(); }
    catch { if (!disposed) error = 'Could not refresh. Please try again.'; }
  }
</script>

<div class="quality-picker" aria-busy={choosing !== null || feed.resolving}>
  <div class="options" role="group" aria-label="Picture quality">
    {#each choices as choice}
      {@const quality = choice.quality}
      <button type="button" aria-pressed={active === quality}
        disabled={choosing !== null || feed.resolving || choice.disabled}
        title={choice.reason || undefined}
        onclick={() => void choose(quality)}>
        <span>{qualityLabel(quality)}</span>
        <span class="state">{choosing === quality ? 'Checking…' : choice.disabled ? choice.reason : active === quality ? feed.activeLabel : 'Select'}</span>
      </button>
    {/each}
  </div>
  <p>Choose a picture quality. We check available copies and prefer efficient files that your device can play.</p>
  <p class="status" role="status">{error || feed.error || (feed.resolving ? 'Finding available qualities…' : '')}</p>
  <button class="refresh" type="button" disabled={choosing !== null || feed.resolving} onclick={() => void refresh()}>Refresh available qualities</button>
</div>

<style>
  .quality-picker { display: grid; gap: var(--space-4); padding: var(--space-4); }
  p { margin: 0; color: var(--text-dim); line-height: 1.5; }
  .options { display: grid; gap: var(--space-2); }
  button { min-height: var(--hit); color: var(--text); background: var(--surface-1); border: 1px solid var(--line); border-radius: var(--radius); padding: var(--space-3) var(--space-4); font: inherit; cursor: pointer; }
  .options button { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
  button:hover:not(:disabled) { background: var(--surface-3); }
  button[aria-pressed='true'] { border-color: var(--focus-ring); background: var(--surface-2); }
  button:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 3px; }
  button:disabled { cursor: default; color: var(--text-faint); }
  .state { font-size: var(--text-sm); text-align: right; max-width: 72%; line-height: 1.3; }
  .status { color: var(--text); min-height: 1.5em; }
  /* Preserve browse sizing; the film's controls should not become a second page. */
  :global([data-watch-space]) .quality-picker,
  :global(.sheet[data-dpad-scope]) .quality-picker { gap: 8px; padding: 8px 12px; font-size: 13px; }
  :global([data-watch-space]) .quality-picker p,
  :global(.sheet[data-dpad-scope]) .quality-picker p { font-size: 12px; line-height: 1.45; }
  :global([data-watch-space]) .options,
  :global(.sheet[data-dpad-scope]) .options { gap: 0; }
  :global([data-watch-space]) .options button,
  :global(.sheet[data-dpad-scope]) .options button {
    min-height: 44px;
    padding: 6px 8px;
    background: transparent;
    border: 0;
    border-radius: var(--radius-xs);
  }
  :global([data-watch-space]) .options button[aria-pressed='true'],
  :global(.sheet[data-dpad-scope]) .options button[aria-pressed='true'],
  :global([data-watch-space]) .options button:hover:not(:disabled),
  :global(.sheet[data-dpad-scope]) .options button:hover:not(:disabled) { background: var(--surface-2); }
  :global([data-watch-space]) .refresh,
  :global(.sheet[data-dpad-scope]) .refresh { padding: 6px 8px; border: 0; background: transparent; color: var(--text-dim); font-size: 12px; text-align: left; }
  .status:empty { display: none; }
</style>
